const Shortvas = require("shortvas");
const ErrorUtil = require("../../../shared/ErrorUtil");
const CanvasUtil = require("./CanvasUtil");

const improveShortvasDrawImage = function() {
	const sampleShortvas = CanvasUtil.getShortvas(
		CanvasUtil.getTransientContext()
	);
	Shortvas.extend({
		drawImage: makeBetterDrawImage(sampleShortvas.drawImage),
	});
};

const makeBetterDrawImage = function(origDrawImage) {
	return function(el, ...args) {
		if (isBrokenImage(el)) {
			ErrorUtil.log(
				"E_CANVAS_BROKEN_IMAGE",
				"Tried to draw broken image via drawImage",
				{src: el.src}
			);
		} else if (isImage(el)) {
			notifyOfDrawImageCall(this, el);
			origDrawImage.call(this, el, ...args);
		} else if (isBrokenCanvas(el)) {
			ErrorUtil.log(
				"E_CANVAS_BROKEN_CANVAS",
				"Tried to draw broken canvas via drawImage"
			);
		} else if (isCanvas(el)) {
			const context = CanvasUtil.getShortvas(el);
			if (args.length < 4) {
				args = [
					args[0],
					args[1],
					args[2] || context.getWidth(),
					args[3] || context.getHeight(),
				];
			}
			origDrawImage.call(this, el, ...args);
		}
	};
};

const contextsWithLoading = new Map();
// Wrap a function drawing things with a Canvas context in this to make it so
// that any drawImage calls for images that haven't loaded yet will be watched
// and a callback will be run when they all load.
// - context: context on which drawImage commands will be run. For anything to
//   work, the context must have the better drawImage method returned by
//   makeBetterDrawImage.
// - startCb: function to call with the context when loading starts, which
//   contains the drawImage commands (among others) to watch for.
// - doneCb: optional, function to call when the loading ends. This will never
//   get called if nothing needs to load. Defaults to startCb in withLoading
//   calls that are not nested.
// Returns a boolean indicating whether startCb drew any images. doneCb will be
// called iff this returns true.
const withLoading = function(context, startCb, doneCb) {
	const loadingInfo = contextsWithLoading.get(context);
	if (loadingInfo) {
		const origImageCount = loadingInfo.elSet.size;
		startCb(context);
		// Nested call; only run doneCb when finished, if any.
		if (doneCb && origImageCount < loadingInfo.elSet.size) {
			loadingInfo.onDoneList.push(doneCb);
			return true;
		}
		return false;
	}

	// Called for each image that loads or errors. Passed the error (or null),
	// but we currently ignore it.
	const onImageLoad = function() {
		imagesLoaded += 1;
		if (imagesLoaded === elSet.size && !done) {
			onDoneList.forEach(f => f(context));
			done = true;
		}
	};

	const onDoneList = [doneCb || startCb];
	const elSet = new Set();
	let imagesLoaded = 0;
	let done = false;

	// Only keep an entry in the map while startCb is running.
	contextsWithLoading.set(context, {
		elSet,
		onImageLoad,
		onDoneList,
	});
	startCb(context);
	contextsWithLoading.delete(context);

	return elSet.size > 0;
};

// Like withLoading, but can be used to wrap drawing code of temp contexts
// which might draw images, in case both temp context and real context need to
// be redrawn when those images finish.
const withLoadingForTemp = function(mainContext, tempContext, startCb, doneCb) {
	const mainContextLoadingInfo = contextsWithLoading.get(mainContext);
	if (!mainContextLoadingInfo) {
		return !!withLoading(tempContext, startCb, doneCb);
	}

	const hasImages = withLoading(tempContext, startCb, () => {
		mainContextLoadingInfo.onImageLoad(null);
		return !!(doneCb && doneCb());
	});

	if (hasImages) {
		// Each call of this function should be treated as unique from other calls
		// with the same mainContext, tempContext pair.  So add a placeholder object to
		// the set of things being loaded to get the set to be the right size
		// without risk of overlap.
		mainContextLoadingInfo.elSet.add({});
		return true;
	}
	return false;
};

// Call within a withLoading block for a context. Provide that context and an
// array of image objects. If any of them are loading, it marks these images
// for loading in the withLoading block and returns true. Otherwise, or if
// the context is not in a withLoading block, it returns false. Can be used
// to avoid drawing things without having their assets ready.
const hasSomeUnloaded = function(context, els) {
	const withLoadingInfo = contextsWithLoading.get(context);
	if (!withLoadingInfo) {
		return false;
	}
	let someNotLoaded = false;
	els.forEach(el => {
		if (isImageToLoad(el)) {
			someNotLoaded = true;
			notifyOfDrawImageCall(context, el);
		}
	});
	return someNotLoaded;
};

//
// Internal helpers
//

const notifyOfDrawImageCall = function(context, el) {
	const withLoadingInfo = contextsWithLoading.get(context);
	const elIsAlreadyLoading = withLoadingInfo && withLoadingInfo.elSet.has(el);
	if (!isImageToLoad(el) || elIsAlreadyLoading) {
		return;
	} else if (!withLoadingInfo) {
		ErrorUtil.log(
			"E_CANVAS_NO_WITH_LOADING",
			"Unloaded image passed to drawImage without withLoading wrapper",
			{src: el.src}
		);
		return;
	}

	const TOO_LONG_SECONDS = 10;
	const tooLongTimeout = setTimeout(() => {
		ErrorUtil.log(
			"E_SLOW_LOADING_IMAGE",
			`Image still not loaded after ${TOO_LONG_SECONDS} seconds`,
			{src: el.src}
		);
	}, TOO_LONG_SECONDS * 1000);

	el.addEventListener(
		"load",
		() => {
			clearTimeout(tooLongTimeout);
			withLoadingInfo.onImageLoad(null);
		},
		{once: true}
	);

	el.addEventListener(
		"error",
		err => {
			clearTimeout(tooLongTimeout);
			withLoadingInfo.onImageLoad(err);
		},
		{once: true}
	);
	withLoadingInfo.elSet.add(el);
};

const isImageToLoad = function(el) {
	return !el.complete && el.src && el.addEventListener;
};

const isImage = function(el) {
	return !!(global.HTMLImageElement && el instanceof global.HTMLImageElement);
};

const isCanvas = function(el) {
	return !!(global.HTMLCanvasElement && el instanceof global.HTMLCanvasElement);
};

const isBrokenImage = function(el) {
	return isImage(el) && el.complete && !el.naturalWidth;
};

const isBrokenCanvas = function(el) {
	return isCanvas(el) && !(el.width > 0 && el.height > 0);
};

module.exports = {
	improveShortvasDrawImage,
	makeBetterDrawImage,
	withLoading,
	withLoadingForTemp,
	hasSomeUnloaded,
	isBrokenCanvas,
};
